import { Paging } from 'api/inc-api.types';

type OfferSMSDisplayInfoType = {
  languageCode: string;
  messageTemplate: string;
};

export type ChannelTemplateSetMap = {
  SMS: OfferSMSDisplayInfoType[];
};

export type OfferDisplayInfo = {
  languageCode: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  smallImage: string;
  mediumImage: string;
};

export type TimeSlot = {
  startTime: string;
  endTime: string;
};

export enum DistributionMode {
  AUTO = 'AUTO',
  ACTIVITY = 'ACTIVITY',
}

export enum RewardType {
  /**
   * A discount on the purchase price.
   */
  DISCOUNT = 'DISCOUNT',
  /**
   * Points Redeeming Discount: 10% off for 50 points (will always have a points cost)
   */
  PRDISCOUNT = 'PRDISCOUNT',
  /**
   * Will discount a fixed amount based on the points ratio and amount of points used
   */
  VARIABLE_PRDISCOUNT = 'VARIABLE_PRDISCOUNT',
  /**
   * Points awarded for the purchase.
   */
  POINTS = 'POINTS',
}

export enum DiscountTarget {
  ITEM = 'ITEM',
  ORDER = 'ORDER',
}

export enum EndDateType {
  NEVER_EXPIRE = 'NEVER_EXPIRE',
  STATIC = 'STATIC',
}

export enum QualifierSelectionMode {
  ONE = 'ONE',
  ALL = 'ALL',
}

export enum ItemAttribute {
  ITEM = 'ITEM',
  GROUP = 'GROUP',
  CATALOG = 'CATALOG',
}

export enum TargetLevel {
  ITEM = 'ITEM',
  MODIFIER = 'MODIFIER',
  BOTH = 'BOTH',
}

export enum DiscountType {
  AMOUNT = 'AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export enum CodeType {
  FIXED = 'FIXED',
  VARIABLE = 'VARIABLE',
}

export enum CodeFormat {
  QRCODE = 'QRCODE',
  BARCODE = 'BARCODE',
}

export enum PurchaseTypes {
  INTEGRATED = 'INTEGRATED',
  STANDALONE = 'STANDALONE',
}

export enum RedistributionMode {
  ACTIVE = 'ACTIVE',
  ONCE = 'ONCE',
}

export enum Channels {
  INAPP = 'INAPP',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  PUSHNOTIFICATION = 'PUSHNOTIFICATION',
}

export enum ScheduleDays {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export enum Operator {
  GT_EQ = 'GT_EQ',
  EQ = 'EQ',
  LT_EQ = 'LT_EQ',
  GT = 'GT',
  LT = 'LT',
}

export enum RedeemType {
  PURCHASE = 'PURCHASE',
  AUTO = 'AUTO',
}

export type Campaign = {
  campaignId: string;
  programId: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  merchantId: string;
  startDate: string;
  endDate: string;
};

export type ListCampaignResult = {
  campaigns: Campaign[];
  paging: Paging;
};

export type CreateCampaignResult = {
  campaignId: string;
};

export type CreateCampaignArgs = {
  title: string;
  shortDescription: string;
  startCampaign: string;
  endCampaign: string;
  startDate: string;
  endDate: string;
};

export type ListCampaignsResult = {
  paging: Paging;
  campaigns: [Campaign];
};

export type ProgramResult = {
  longDescription: string;
  programId: string;
  shortDescription: string;
  title: string;
};

export type ListProgramsResult = {
  paging: Paging;
  programs: [ProgramResult];
};

export type DiscountItemQualifierType = {
  qualifierSelectionMode: QualifierSelectionMode;
  qualifierItems: QualifierItemType[];
};

type ExternalIdMapType = {
  [key: string]: string;
};

export type OffersResult = {
  offerId: string;
  campaignId: string;
  campaignTitle: string;
  programId: string;
  title: string;
  offerStatus: OfferStatusType;
  offerType: string;
  numOffersToDistribute: number;
  numOffersInInventory: number;
  externalIdMap: ExternalIdMapType;
  priority: number;
  channelTemplateSetMap: ChannelTemplateSetMap;
  redemptionEndDateType: EndDateType | null;
  redemptionStartDate: string;
  redemptionEndDate: string | null;
  redemptionEndTime: string;
  distributionEndDateType: EndDateType | null;
  distributionStartDate: string;
  distributionEndDate: string;
  awardPointsAsPending: boolean;
  distributionMode: DistributionMode;
  displayInfo: OfferDisplayInfo[];
  offerReward: {
    rewardType: RewardType;
    discount: {
      extDiscountId: string;
      discountTarget: DiscountTarget;
      discountItemQualifier: DiscountItemQualifierType;
      discountType: DiscountType;
      discountAmount: number;
      discountPercentage: number;
    };
    qualifier: string;
    pointsReward: number;
    awardPointsAsPending: boolean;
    pointsCost: number;
    awardForEachQualifyingUnit: boolean;
  };
  merchantId: string;
  codeType: CodeType;
  codeFormat: CodeFormat;
  offerCode: string | null;
  distributionRuleSet: string;
  redemptionRuleSet: string;
  createdDate: string;
  updatedDate: string;
  isLoyaltyOffer: boolean;
  redeemType: RedeemType;
  purchaseTypes: PurchaseTypes[];
  activityNames: string[];
  enableReach: boolean;
  reach: string;
  reachDisplayInfo: string;
  offerRedemptionExpired: boolean;
  isDraft?: boolean;
  draftOfferId?: string | null;
  sourceOfferId?: string | null;
};

export type ListOffersResult = {
  paging: Paging;
  offers: OffersResult[];
};

export enum OfferStatusType {
  ACTIVE = 'ACTIVE',
  SCHEDULED = 'SCHEDULED',
  EXPIRED = 'EXPIRED',
  DRAFT = 'DRAFT',
  ALL = 'ALL',
}

export type OfferParamsType = {
  count: number;
  page: number;
  campaignId?: string;
  sortBy?: string;
  sortDirection?: string;
  loyaltyoffers?: string;
  title?: string;
  offerstatus?: OfferStatusType | 'ALL';
};

type QualifierItemType = {
  attributeId: string;
  attributeObjectName: string;
  count: number;
  extAttributeId: string;
  itemAttribute: ItemAttribute;
  targetLevel: TargetLevel;
};

export type GetOfferResult = {
  offerId: string;
  campaignId: string;
  campaignTitle: string;
  programId: string;
  title: string;
  offerStatus: OfferStatusType;
  offerType: string;
  numOffersToDistribute: number;
  numOffersInInventory: number;
  externalIdMap: string | null;
  priority: number;
  channelTemplateSetMap: ChannelTemplateSetMap;
  redemptionEndDateType: EndDateType | null;
  redemptionStartDate: string;
  redemptionEndDate: string | null;
  redemptionEndTime: string | null;
  distributionEndDateType: EndDateType | null;
  distributionStartDate: string;
  distributionEndDate: string | null;
  awardPointsAsPending: boolean;
  distributionMode: DistributionMode;
  displayInfo: OfferDisplayInfo[];
  offerReward: {
    rewardType: RewardType;
    discount: {
      extDiscountId: string | null;
      discountTarget: DiscountTarget;
      discountItemQualifier: DiscountItemQualifierType;
      discountType: DiscountType;
      discountAmount: number;
      discountPercentage: number;
    };
    qualifier: {
      qualifierSelectionMode: QualifierSelectionMode;
      qualifierItems: [
        {
          attributeId: string;
          attributeObjectName: string;
          count: number;
          extAttributeId: string;
          itemAttribute: ItemAttribute;
          targetLevel: TargetLevel;
        },
      ];
    };
    pointsReward: number;
    awardPointsAsPending: boolean;
    pointsCost: number;
    awardForEachQualifyingUnit: boolean;
  };
  merchantId: string;
  codeType: CodeType;
  codeFormat: CodeFormat;
  offerCode: string;
  distributionRuleSet: string;
  redemptionRuleSet: string;
  createdDate: string;
  updatedDate: string;
  isLoyaltyOffer: boolean;
  redeemType: RedeemType;
  purchaseTypes: PurchaseTypes[];
  activityNames: string[];
  enableReach: boolean;
  reach: string;
  reachDisplayInfo: string | null;
  offerRedemptionExpired: boolean;
  distributionStores: string[];
  redemptionStores: string[];
  autoRedeem?: boolean;
  redistributionMode?: RedistributionMode;
  redistributionTimeout?: number;
  channels?: Channels[];
  formattingSetting?: {
    codeFormat: CodeFormat;
    codeType: CodeType;
    fixedCode: string | null;
    prefix: string | null;
    trailingCodeLength: number | null;
  };
  distributionSchedule?: {
    startDate: string;
    endDate: string | null;
    scheduleDays: ScheduleDays[];
    timeSlots: TimeSlot[];
  };
  distributionJobId?: string;
  distributionPersonaId?: null;
  redemptionPersonaId?: null;
  timezone?: string;
  enableGuestCheckout?: boolean;
};

export type GetOfferDistributionSettingsResult = {
  distributionMode: DistributionMode;
  autoRedeem: boolean;
  redistributionMode: RedistributionMode;
  redistributionTimeout: number;
  channels: Channels[];
  channelTemplateSetMap: ChannelTemplateSetMap;
  numOffersToDistribute: number;
  formattingSetting: {
    codeFormat: CodeFormat;
    codeType: CodeType;
    fixedCode: string | null;
    prefix: string | null;
    trailingCodeLength: number | null;
  };
  distributionSchedule: {
    startDate: string;
    endDate: string | null;
    scheduleDays: ScheduleDays[];
    timeSlots: TimeSlot[];
  };
  distributionJobId: string;
  redemptionStartDate: string;
  redemptionEndDate: string | null;
  distributionStartDate: string;
  distributionEndDate: string | null;
  distributionRuleSet: string;
  redemptionRuleSet: string;
  distributionPersonaId: null;
  redemptionPersonaId: null;
  timezone: string;
  distributionEndDateType: EndDateType | null;
  redemptionEndDateType: EndDateType | null;
  redemptionEndTime: null;
  enableGuestCheckout: boolean;
};

type SharedOfferBody = {
  offerType: string;
  title: string;
  isActive: boolean;
  externalIdMap: string | null;
  distributionSetting: {
    redistributionMode: RedistributionMode;
    redemptionStartDate: string;
    redemptionEndDate: string | null;
    redemptionEndDateType: EndDateType | null;
    redemptionEndTime: null;
    redistributionTimeout: number | null;
    distributionMode: DistributionMode;
    numOffersToDistribute: number | string;
    distributionChannels: Channels;
    distributionStartDate: string;
    distributionEndDate: string | null;
    distributionEndDateType: EndDateType | null;
    distributionSchedule: {
      startDate: string;
      endDate: string | null;
      scheduleDays: ScheduleDays[];
      timeSlots: TimeSlot[];
    };
    redemptionRuleSet: {
      ruleType: string;
      expressionName: string;
      operator: Operator;
      expressionValue: string;
      extendedAttributes: {
        attributeTitle: string;
      };
    };
    formattingSetting: {
      codeFormat: CodeFormat;
      codeType: CodeType;
      fixedCode: string;
    };
    distributionRuleSet: {
      ruleType: string;
      expressionName: string;
      operator: Operator;
      expressionValue: string;
      extendedAttributes: {
        attributeTitle: string;
      };
    };
    timezone: string;
    channelTemplateSetMap: ChannelTemplateSetMap;
  };
  displayInfo: OfferDisplayInfo[];
  redeemType: RedeemType;
  purchaseTypes: PurchaseTypes[];
  rewardDTO: {
    awardForEachQualifyingUnit: boolean;
    rewardType: RewardType;
    discount: {
      discountTarget: DiscountTarget;
      discountType: DiscountType;
      discountPercentage: number;
      discountItemQualifier: DiscountItemQualifierType;
    };
    qualifier: {
      qualifierSelectionMode: QualifierSelectionMode;
      qualifierItems: [
        {
          attributeId: string;
          attributeObjectName: string;
          count: number;
          extAttributeId: string;
          itemAttribute: ItemAttribute;
          targetLevel: TargetLevel;
        },
      ];
    };
  };
  activityNames: string[];
  enableReach: boolean;
  distributionStores: string[];
  redemptionStores: string[];
  reach: string;
  templateId?: string;
  isLoyaltyOffer?: boolean;
};

export type CreateOfferBody = SharedOfferBody & {
  campaignId: string;
  programId: string;
  priority: number;
};

export type UpdateOfferBody = SharedOfferBody & { offerId: string };

export type CreateDraftOfferBody = SharedOfferBody & {
  campaignId: string;
  programId: string;
  priority: number;
};

export type UpdateDraftOfferBody = SharedOfferBody & {
  draftOfferId: string;
  campaignId?: string;
  programId?: string;
};

export type GetDraftOfferResult = GetOfferResult & {
  draftOfferId: string;
};

export type PublishDraftOfferResult = {
  offerId: string;
};

export type VisibilityKeys =
  | 'segment.targeting'
  | 'message.auto.targeting'
  | 'message.activity.targeting'
  | 'offer.auto.targeting'
  | 'offer.activity.targeting'
  | 'offer.redemption'
  | 'loyalty.auto.targeting'
  | 'loyalty.activity.targeting'
  | 'loyalty.redemption';
